import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

interface IFormInput {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

interface PageTheme {
  name: string;
  backgroundColor: string;
  borderColor: string;
  primaryColor: string;
  buttonTextColor: string;
  textColor: string;
  fieldBorderColor: string;
  containerColor: string;
  containerTextColor: string;
}

interface Props {
  theme?: PageTheme;
  onConfirmation: (data: IFormInput) => void;
}

// Yup validation schema
const schema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string()
    .matches(/^\d{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  email: Yup.string().email('Invalid email').required('Email is required')
}).required();

// ConfirmationForm Component
const ConfirmationForm = ({ theme, onConfirmation }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IFormInput>({
    // @ts-ignore
    resolver: yupResolver(schema)
  });

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    onConfirmation(data);
  };

  return (
    <div
      className="transition-all duration-300 ease-in-out p-4 w-full px-8 py-6 mb-10 shadow-lg rounded-2xl mt-4"
      style={{
        backgroundColor: theme?.containerColor || 'white',
        borderColor: theme?.primaryColor || '#ddd',
        border: `2px solid ${theme?.borderColor || '#ddd'}`
      }}
    >
      <h1
        className="text-center text-xl font-bold mt-8"
        style={{ color: theme?.textColor || '#333' }}
      >
        Confirm Your Booking
      </h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <input
          type="text"
          {...register('firstName')}
          placeholder="First Name"
          className="px-3 py-3 mt-4 rounded-lg text-base w-full border-2"
          style={{
            borderColor: theme?.fieldBorderColor || '#ddd',
            outlineColor: theme?.primaryColor || '#4450ce',
            backgroundColor: '#fff'
          }}
        />
        {errors.firstName && (
          <span className="block text-sm mt-2 text-red-600">{errors.firstName.message}</span>
        )}

        <input
          type="text"
          {...register('lastName')}
          placeholder="Last Name"
          className="px-3 py-3 mt-4 rounded-lg text-base w-full border-2"
          style={{
            borderColor: theme?.fieldBorderColor || '#ddd',
            outlineColor: theme?.primaryColor || '#4450ce',
            backgroundColor: '#fff'
          }}
        />
        {errors.lastName && (
          <span className="block text-sm mt-2 text-red-600">{errors.lastName.message}</span>
        )}

        <input
          type="tel"
          {...register('phone')}
          placeholder="Phone Number"
          className="px-3 py-3 mt-4 rounded-lg text-base w-full border-2"
          style={{
            borderColor: theme?.fieldBorderColor || '#ddd',
            outlineColor: theme?.primaryColor || '#4450ce',
            backgroundColor: '#fff'
          }}
        />
        {errors.phone && (
          <span className="block text-sm mt-2 text-red-600">{errors.phone.message}</span>
        )}

        <input
          type="email"
          {...register('email')}
          placeholder="Email Address"
          className="px-3 py-3 mt-4 rounded-lg text-base w-full border-2"
          style={{
            borderColor: theme?.fieldBorderColor || '#ddd',
            outlineColor: theme?.primaryColor || '#4450ce',
            backgroundColor: '#fff'
          }}
        />
        {errors.email && (
          <span className="block text-sm mt-2 text-red-600">{errors.email.message}</span>
        )}

        <button
          type="submit"
          className="px-4 py-4 mt-4 rounded-lg text-base w-full cursor-pointer"
          style={{
            backgroundColor: theme?.primaryColor || '#4450ce',
            color: theme?.buttonTextColor || 'white'
          }}
        >
          Confirm Booking
        </button>
      </form>
    </div>
  );
};

export default ConfirmationForm;
